import { useEffect, useRef, useState } from "react";
import { Mail } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/features/auth/AuthContext";
import { isFirebaseConfigured } from "@/features/auth/firebase/config";
import {
  clearStoredSignInEmail,
  completeEmailSignIn,
  hrefIsEmailSignInLink,
  readStoredSignInEmail,
  sendEmailSignInLink,
  signInWithGoogle,
} from "@/features/auth/firebase/client";
import { messageForSignInError } from "@/features/auth/firebase/errors";
import { postLoginRedirect } from "@/lib/extensionConnect";
import { BrandLockup } from "@/shared/ui/brand";
import { Button } from "@/shared/ui/button";
import { ErrorAlert } from "@/shared/ui/error-alert";
import { Input } from "@/shared/ui/input";
import { useI18n } from "@/i18n/I18nContext";

export function LoginPage() {
  const { t } = useI18n();
  const { establishSession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [showEmail, setShowEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const isVerification = window.location.pathname === "/auth/verify";
  const attemptedVerification = useRef(false);
  const firebaseReady = isFirebaseConfigured();

  const finishEmailSignIn = async (address: string) => {
    if (!hrefIsEmailSignInLink(window.location.href)) {
      setError(t("login.invalidLink"));
      return;
    }
    if (!address.includes("@")) {
      setError(t("login.enterEmailReceived"));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await completeEmailSignIn(address, window.location.href);
      await establishSession();
      clearStoredSignInEmail();
      navigate(postLoginRedirect(params.toString()), { replace: true });
    } catch (err) {
      setError(messageForSignInError(err, t("login.completeFailed"), t));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isVerification || attemptedVerification.current) return;
    const storedEmail = readStoredSignInEmail();
    if (!hrefIsEmailSignInLink(window.location.href)) {
      setError(t("login.invalidLink"));
      return;
    }
    if (!storedEmail) {
      setShowEmail(true);
      return;
    }
    attemptedVerification.current = true;
    void finishEmailSignIn(storedEmail);
    // This effect intentionally runs once when Firebase's email-link page opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVerification]);

  const completeGoogle = async () => {
    setError(null);
    setLoading(true);
    try {
      await signInWithGoogle();
      await establishSession();
      navigate(postLoginRedirect(params.toString()), { replace: true });
    } catch (err) {
      setError(messageForSignInError(err, t("login.googleFailed"), t));
    } finally {
      setLoading(false);
    }
  };

  const submitEmail = async (event: React.FormEvent) => {
    event.preventDefault();
    const address = email.trim();
    if (!address.includes("@")) {
      setError(t("login.invalidEmail"));
      return;
    }
    if (isVerification) {
      await finishEmailSignIn(address);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const continueUrl = new URL("/auth/verify", window.location.origin);
      continueUrl.searchParams.set("redirect", postLoginRedirect(params.toString()));
      await sendEmailSignInLink(address, continueUrl.toString());
      setSent(true);
    } catch (err) {
      setError(messageForSignInError(err, t("login.sendFailed"), t));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative flex min-h-dvh items-center justify-center bg-canvas px-6 py-16">
      <a href="/login" className="absolute left-6 top-6 flex items-center gap-2 text-foreground no-underline sm:left-10 sm:top-8">
        <BrandLockup wordmarkClassName="text-[18px]" />
      </a>

      <section className="flex w-full max-w-[392px] flex-col items-center gap-7 text-center" aria-labelledby="sign-in-title">
        <div className="flex flex-col items-center gap-2.5">
          <h1 id="sign-in-title" className="text-[22px] font-bold tracking-tight text-foreground">{isVerification ? t("login.completeTitle") : sent ? t("login.checkInbox") : t("login.title")}</h1>
          <p className="max-w-80 text-sm leading-5 text-muted-foreground">
            {isVerification ? t("login.confirmEmail") : sent ? t("login.sentTo", { email }) : t("login.tagline")}
          </p>
        </div>

        <div className="w-full rounded-xl border border-border bg-card p-7 text-left shadow-[0_1px_2px_rgb(24_24_27_/_0.04)]">
          {sent ? (
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><Mail className="size-5" /></div>
              <p className="text-sm leading-5 text-muted-foreground">{t("login.linkExpires")}</p>
              <Button type="button" variant="link" className="h-auto p-0 text-sm font-semibold" onClick={() => setSent(false)}>{t("login.useAnother")}</Button>
            </div>
          ) : showEmail ? (
            <form className="flex flex-col gap-4" onSubmit={(event) => void submitEmail(event)} noValidate>
              <label className="flex flex-col gap-2 text-sm font-medium text-foreground" htmlFor="email">
                {t("login.emailAddress")}
                <Input id="email" type="email" autoComplete="email" autoFocus placeholder="name@company.com" value={email} disabled={loading} onChange={(event) => setEmail(event.target.value)} />
              </label>
              <Button type="submit" className="h-11 w-full" loading={loading} disabled={!firebaseReady}>{isVerification ? t("login.continue") : t("login.sendLink")}</Button>
              {!isVerification ? <Button type="button" variant="ghost" className="h-auto py-2 text-sm font-medium text-muted-foreground hover:text-foreground" onClick={() => { setShowEmail(false); setError(null); }}>{t("login.back")}</Button> : null}
            </form>
          ) : (
            <div className="flex flex-col gap-3.5">
              <Button type="button" variant="outline" className="h-11 w-full border-border bg-card font-semibold hover:bg-accent" disabled={loading || !firebaseReady} onClick={() => void completeGoogle()}>
                <img src="/assets/google-icon.svg" alt="" className="size-[18px]" /> {t("login.google")}
              </Button>
              <div className="flex items-center gap-3"><span className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">{t("login.or")}</span><span className="h-px flex-1 bg-border" /></div>
              <Button type="button" variant="ghost" className="h-11 text-sm font-semibold text-muted-foreground hover:text-foreground" onClick={() => setShowEmail(true)}>{t("login.email")}</Button>
            </div>
          )}
          {error ? <ErrorAlert message={error} compact className="mt-4 text-left" /> : null}
          {!firebaseReady && !sent ? <p className="mt-4 text-center text-xs text-muted-foreground">{t("login.unavailable")}</p> : null}
        </div>

        <p className="max-w-80 text-xs leading-5 text-muted-foreground">{t("login.agreePrefix")}<a href="#terms">{t("login.terms")}</a>{t("login.and")}<a href="#privacy">{t("login.privacy")}</a>{t("login.agreeSuffix")}</p>
      </section>
      <p className="absolute bottom-7 text-xs text-muted-foreground">© 2026 Memoru</p>
    </main>
  );
}
